/**
 * ChangedFieldHighlight - Amber-highlighted wrapper for a changed field.
 *
 * Shows the field label + change indicator + children (value display)
 * inside an amber background, with an optional transfer checkbox on the right.
 */

import type { EntityChanges } from '../utils/useEntityChanges';
import type { TransferChecklist } from '../utils/useTransferChecklist';
import { ChangedFieldIndicator } from './ChangeIndicators';
import { TransferCheckbox } from './TransferCheckbox';

interface ChangedFieldHighlightProps {
  label: string;
  fieldKey: string;
  changes: EntityChanges;
  checklist: TransferChecklist;
  children: React.ReactNode;
}

export const ChangedFieldHighlight: React.FC<ChangedFieldHighlightProps> = ({
  label,
  fieldKey,
  changes,
  checklist,
  children,
}) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 rounded-md px-3 py-2 bg-amber-50/60">
      <div className="flex items-center gap-1 mb-0.5">
        <span className="text-xs font-medium text-gray-500">{label}</span>
        <ChangedFieldIndicator
          oldValue={changes.getOldValue(fieldKey)}
          label={label}
        />
      </div>
      {children}
    </div>
    {checklist.showCheckboxes && (
      <TransferCheckbox
        checked={checklist.isChecked(fieldKey)}
        onToggle={() => checklist.toggle(fieldKey)}
        className="mt-2.5"
      />
    )}
  </div>
);
